import React from 'react';
import { getExpirationStatus, formatExpirationDate } from '../../utils/creditExpiration';
import { useTranslation } from 'react-i18next';

/**
 * CreditExpirationBadge Component
 * Displays expiration status for a credit with appropriate styling
 * 
 * @param {Object} props
 * @param {Object} props.credit - The credit object
 * @param {boolean} props.showDate - Whether to show the full expiration date (default: true)
 * @param {boolean} props.compact - Use compact display mode (default: false)
 */
export const CreditExpirationBadge = ({ credit, showDate = true, compact = true }) => {
  const { t } = useTranslation();
  
  if (!credit?.expiresAt) {
    return null; // Don't show anything if no expiration
  }
  
  const status = getExpirationStatus(credit);
  
  // Color mapping for badges
  const colorClasses = {
    red: 'bg-pdred/10 text-pdred border-pdred',
    orange: 'bg-pdorange/10 text-pdorange border-pdorange',
    gray: 'bg-pdgray/10 text-pdgray border-pdgray'
  };
  
  const badgeClass = colorClasses[status.color] || colorClasses.gray;
  
  if (compact) {
    return (
      <div className={`inline-flex w-fit items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold ${badgeClass}`}>
        {status.badge || status.text}
      </div>
    );
  }
  
  return (
    <div className="flex flex-col gap-1">
      {status.badge && (
        <span className={`inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs font-bold ${badgeClass}`}>
          {t(status.badge)}
        </span>
      )}
      {showDate && (
        <span className={`text-sm w-fit  ${status.isExpired ? 'text-pdred font-semibold' : 'text-gray-600'}`}>
          {status.isExpired ? t('Expired on') : t('Expires on')}: {formatExpirationDate(credit)}
        </span>
      )}
      {!showDate && !status.badge && (
        <span className={`text-sm w-fit  ${status.color === 'red' ? 'text-pdred' : status.color === 'orange' ? 'text-pdorange' : 'text-gray-600'}`}>
          {t(status.text)}
        </span>
      )}
    </div>
  );
};

